/* import __COLOCATED_TEMPLATE__ from './suggestion-list.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { type Topic } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import { taskFor } from 'ember-concurrency-ts';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';

interface Args {
  selectedTopic: Topic;
  selectedSubTopic: string | null;
  showTopics?: boolean;
}

export default class SuggestionList extends Component<Args> {
  @service declare appService: AppService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare contentSuggestionsService: ContentSuggestionsService;

  @tracked declare listApi: AiContentLibraryApi;

  suggestionsData = DeduplicatedAsyncData(
    this,
    () => [this.args.selectedTopic.name, this.args.selectedSubTopic],
    (topic: string, subtopic: string | null) => {
      return taskFor(this.contentSuggestionsService.loadSuggestionsForTopic).perform(
        topic,
        subtopic,
      );
    },
  );

  private readonly boundReloadSuggestions: () => void;

  constructor(owner: any, args: any) {
    super(owner, args);
    this.boundReloadSuggestions = this.reloadSuggestions.bind(this);
    this.knowledgeHubDrawerEditorService.on('onDrawerClose', this.boundReloadSuggestions);
  }

  willDestroy() {
    super.willDestroy();
    this.knowledgeHubDrawerEditorService.off('onDrawerClose', this.boundReloadSuggestions);
  }

  get suggestions() {
    return this.contentSuggestionsService.suggestionsForTopic;
  }

  get isLoadingSuggestions() {
    return this.suggestionsData.isLoading;
  }

  @action
  reloadSuggestions() {
    // Trigger a reload
    this.suggestionsData.reload(this.args.selectedTopic.name, this.args.selectedSubTopic);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::SuggestionList': typeof SuggestionList;
  }
}
